<template>
  <section v-if="isGetCollectionRequestPending || configurationsPending">
    <p-loading />
  </section>
  <section v-else class="organizations">
    <div class="organization-list-wrapper">
      <div class="header-wrapper">
        <div>
          <router-link v-if="$hasPermission('organizations')" to="organizations/new">+&nbsp;add new</router-link>
        </div>
        <div><label>Total:</label>&nbsp;{{ total }}</div>
      </div>
      <ul class="organization-list">
        <li class="organization-list-item header">
          <!-- <div>Id</div> -->
          <div>Name</div>
          <div>Type</div>
          <div>Aliases</div>
          <div>Prefix</div>
          <div>Correspondence domains</div>
          <div>Allowed models</div>
          <div>Customer numbers</div>
          <div>Speech to text</div>
          <div>OCR Private</div>
          <div>Last Modified</div>
          <div class="action-link-wrapper"></div>
        </li>
      </ul>
      <ul class="organization-list">
        <li v-for="organization in collection" :key="organization.id" class="organization-list-item">
          <!-- <div>{{ entry.id }}</div> -->
          <div>{{ organization.name }}</div>
          <div>{{ organization.type || '' }}</div>
          <div>
            <ul>
              <li v-for="item of organization.aliases" :key="organization.id + item">
                <label>{{ item }}</label>
              </li>
            </ul>
          </div>
          <div>{{ organization.shortName }}</div>
          <div>
            <ul>
              <li v-for="item of organization.domains" :key="organization.id + item">
                <label>{{ item }}</label>
              </li>
            </ul>
          </div>
          <div>
            <ul class="models-list">
              <li v-for="model in getModels(organization)" :key="model">
                {{ model }}
              </li>
            </ul>
          </div>
          <div>
            <ul class="models-list">
              <li v-for="customerNumber in organization.customerNumbers || []" :key="customerNumber">
                {{ customerNumber }}
              </li>
            </ul>
          </div>
          <div>{{ getSttLabel(organization) }}</div>
          <div>{{ getPrivateOcrLabel(organization) }}</div>
          <div>
            {{ organization.updatedAt ? new Date(organization.updatedAt).toLocaleString() : new
              Date(organization.createdAt).toLocaleString() }}
          </div>
          <div class="action-link-wrapper">
            <router-link :to="`organizations/${organization.id}/edit`">&#9998;</router-link>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Loading from '@/components/common/Loading';

export default {
  components: {
    'p-loading': Loading
  },
  data() {
    return {};
  },
  computed: {
    ...mapState({
      total: (s) => s.organizations.collection.length,
      collection: (s) => s.organizations.collection,
      models: (s) => s.nlp.models || [],
      modelPermissions: (s) => s.nlp.modelPermissions || [],
      configurationsPending: (s) => s.nlp.configurationsPending,
      isGetCollectionRequestPending: (s) => s.organizations.isGetCollectionRequestPending,
      sttServices: (s) => s.nlp.sttServices
    })
  },
  watch: {
    $route: {
      async handler() {
        await Promise.all([
          this.$store.dispatch('nlp/getConfigurations'),
          this.$store.dispatch('organizations/getCollection'),
          this.$store.dispatch('nlp/getSttServices')
        ]);
      },
      immediate: true
    }
  },
  methods: {
    getModels(organization) {
      return this.modelPermissions
        .filter((permission) => permission.organization == organization.id)
        .map((permission) => this.models.find((model) => model.id == permission.model))
        .filter((model) => model)
        .map((model) => (model ? (model.version ? `${model.name} (${model.version})` : model.name) : 'Unknown Model'));
    },
    getSttLabel(organization) {
      if (!organization.enableSpeechToText) return '';

      if (organization.speechToTextService && organization.speechToTextService.type === 'service') {
        const name = this.sttServices?.find((s) => s.id === organization.speechToTextService.id)?.name || organization.speechToTextService.id;
        return `${name}`;
      }

      if (organization.speechToTextService && organization.speechToTextService.type === 'model') {
        const modelName = this.models?.find((m) => m.id === organization.speechToTextService.id)?.name || organization.speechToTextService.id;
        let label = `${modelName}`;
        if (organization.speechPostProcessingModel && organization.speechPostProcessingModel.id) {
          const postModelName =
            this.models?.find((m) => m.id === organization.speechPostProcessingModel.id)?.name || organization.speechPostProcessingModel.id;
          label += ` \\ ${postModelName}`;
        }
        return label;
      }

      if (organization.speechToTextService && organization.speechToTextService.id) {
        return organization.speechToTextService.id;
      }

      return '';
    },
    getPrivateOcrLabel(organization) {
      return organization.enablePrivateOcr ? 'Enabled' : '';
    }
  }
};
</script>

<style lang="scss" scoped>
.organizations {
  display: grid;
  padding: 1rem 1rem 0 1rem;
  // width: 100%;

  .organization-list-wrapper {
    display: grid;
    grid-template-rows: 20px max-content minmax(0, 1fr);

    .header-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      font-size: 0.75rem;
      margin: 0;
      padding: 0;
      font-weight: 400;
      margin-right: 6px;

      label {
        font-weight: 500;
      }

      >* {
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
      }
    }
  }

  .organization-list {
    margin: 0;
    padding: 0;
    list-style: none;

    .organization-list-item {
      display: grid;
      grid-template-columns: minmax(150px, 1fr) 80px 100px 100px 200px 200px 150px 100px 100px 160px 30px;
      grid-gap: 0;
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;

      >* {
        padding: 0.5rem 0.5rem;
        border-left: 1px solid var(--theme-highlight);

        &:last-child {
          border-right: 1px solid var(--theme-highlight);
        }
      }

      &:first-child {
        border-top: 1px solid var(--theme-highlight);
      }

      &.header {
        font-size: 0.75rem;
        color: var(--theme-on-background-accent);

        >* {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          padding: 0.25rem 0.5rem;
        }
      }

      .action-link-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        border-left-color: transparent;

        >* {
          opacity: 0;
        }
      }

      &:hover {
        background-color: var(--theme-highlight);

        .action-link-wrapper {
          >* {
            opacity: 1;
          }
        }
      }

      .more-info {
        font-style: italic;
        color: var(--theme-on-background-accent);
      }
    }

    &:not(:last-child) {
      margin-right: 6px;
    }

    &:last-child {
      overflow-y: scroll;
    }
  }

  .models-list {
    display: flex;
    width: 150px;
    flex-wrap: wrap;
    list-style: none;
    padding: 0;
    gap: 5px;
  }

  .models-list li {
    border-radius: 2px;
    background: var(--theme-primary);
    padding: 0.25rem;
  }
}
</style>
